import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface ServiceCardProps {
  icon: string;
  title: string;
  description: string;
  features: string[];
  from: string;
  tabKey: string;
  colorClass: string;
}

function ServiceCard({ icon, title, description, features, from, tabKey, colorClass }: ServiceCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get benefit text based on service
  const getBenefit = () => {
    switch(tabKey) {
      case 'website':
        return 'Get a professional, fast-loading website that converts visitors into customers.';
      case 'smm':
        return 'Grow your social media presence and engagement with targeted content strategies.';
      case 'traffic':
        return 'Drive quality traffic to your business with targeted advertising campaigns.';
      default:
        return '';
    }
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-1 transition-all duration-300 group flex flex-col">
      <div className={`h-3 w-full ${colorClass}`}></div>
      <div className="p-8 flex flex-col flex-1">
        <div className={`w-14 h-14 flex items-center justify-center rounded-2xl mb-6 ${colorClass} bg-opacity-10`}>
          <i className={`${icon} text-2xl text-clairBlue-500`}></i>
        </div>
        <h3 className="font-display text-xl font-bold text-darkBlue-500 mb-3">{title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed mb-4">{description}</p>
        <p className="text-clairBlue-600 text-sm font-medium mb-6 bg-clairBlue-50 p-3 rounded-lg">{getBenefit()}</p>
        <ul className="space-y-2.5 mb-8 flex-1">
          {features.map((f, i) => (
            <li key={i} className="flex items-center gap-2.5 text-sm text-gray-600">
              <div className="w-5 h-5 flex items-center justify-center rounded-full bg-clairBlue-50 flex-shrink-0">
                <i className="ri-check-line text-clairBlue-500 text-xs"></i>
              </div>
              {f}
            </li>
          ))}
        </ul>
        <div className="border-t border-gray-100 pt-5 mt-auto">
          <button
            onClick={() => navigate(`/services?tab=${tabKey}`)}
            className="w-full flex items-center justify-center gap-2 bg-darkBlue-500 hover:bg-darkBlue-600 text-white font-semibold text-sm px-6 py-3 rounded-xl transition-all hover:scale-105"
          >
            Start Your Project
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ServicesPreview() {
  const { t } = useTranslation();

  const services = [
    {
      icon: 'ri-global-line',
      title: t('services.websiteTitle'),
      description: t('services.websiteDesc'),
      features: [t('services.websiteFeature1'), t('services.websiteFeature2'), t('services.websiteFeature3'), t('services.websiteFeature4')],
      from: t('services.websiteFrom'),
      tabKey: 'website',
      colorClass: 'bg-clairBlue-300',
    },
    {
      icon: 'ri-megaphone-line',
      title: t('services.smmTitle'),
      description: t('services.smmDesc'),
      features: [t('services.smmFeature1'), t('services.smmFeature2'), t('services.smmFeature3'), t('services.smmFeature4')],
      from: t('services.smmFrom'),
      tabKey: 'smm',
      colorClass: 'bg-darkBlue-400',
    },
    {
      icon: 'ri-line-chart-line',
      title: t('services.trafficTitle'),
      description: t('services.trafficDesc'),
      features: [t('services.trafficFeature1'), t('services.trafficFeature2'), t('services.trafficFeature3'), t('services.trafficFeature4')],
      from: t('services.trafficFrom'),
      tabKey: 'traffic',
      colorClass: 'bg-clairBlue-400',
    },
  ];

  return (
    <section id="services" className="py-24 bg-brokenWhite-100">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-block text-clairBlue-500 text-xs font-bold uppercase tracking-widest mb-4 bg-clairBlue-50 px-4 py-1.5 rounded-full">
            {t('services.label')}
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-darkBlue-500 mb-5">
            Our Digital Services
          </h2>
          <p className="text-gray-500 text-lg leading-relaxed">Professional solutions to help your business grow online and reach more customers.</p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((svc) => (
            <ServiceCard key={svc.tabKey} {...svc} />
          ))}
        </div>
      </div>
    </section>
  );
}
